import { groupBy, mapValues, sum, uniq, uniqWith } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { distanceEquals } from '@paragon/geometry-library';
import { sortBy } from '@paragon/utilities';

import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';
import {
  getAggregatedTrussStatisticsByBundleGuid,
  getBundlesByScheduleLineGroupGuid,
  getStatusesByBundleGuid,
} from './bundles';

export const getScheduleLineGroupsByLineGuid = createSelector(
  (state: RootState) => state.scheduleLineGroups,
  (state: RootState) => state.lines,

  (scheduleLineGroups, lines) => {
    const scheduleLineGroupsByLineGuid = groupBy(
      Object.values(scheduleLineGroups),
      (scheduleLineGroup) => scheduleLineGroup.lineGuid,
    );
    return mapValues(lines, (line) =>
      sortBy(scheduleLineGroupsByLineGuid[line.guid] ?? [], (scheduleLineGroup) => scheduleLineGroup.priority),
    );
  },
);

export const getAggregatedTrussStatisticsByScheduleLineGroupGuid = createSelector(
  (state: RootState) => state.scheduleLineGroups,
  getBundlesByScheduleLineGroupGuid,
  getAggregatedTrussStatisticsByBundleGuid,

  (
    scheduleLineGroups,
    bundlesByScheduleLineGroupGuid,
    aggregatedTrussStatisticsByBundleGuid,
  ): { [scheduleLineGroupGuid: string]: AggregatedTrussStatistics } =>
    mapValues(scheduleLineGroups, (scheduleLineGroup) => {
      // Do not show values unless all are loaded - we do not want to provide misleading information
      const allTrussesForScheduleLineGroupAreLoaded = bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid].every(
        (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses.length > 0,
      );
      if (!allTrussesForScheduleLineGroupAreLoaded)
        return {
          boardFeet: null,
          linearFeet: null,
          trussCount: sum(
            bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
              (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
            ),
          ),
          thicknesses: [],
          widths: [],
          heights: [],
        };

      return {
        boardFeet: sum(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].boardFeet ?? 0,
          ),
        ),
        linearFeet: sum(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].linearFeet ?? 0,
          ),
        ),
        trussCount: sum(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].trussCount,
          ),
        ),
        widths: uniqWith(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].widths,
          ),
          distanceEquals,
        ),
        heights: uniqWith(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].heights,
          ),
          distanceEquals,
        ),
        thicknesses: uniqWith(
          bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
            (bundle) => aggregatedTrussStatisticsByBundleGuid[bundle.guid].thicknesses,
          ),
          distanceEquals,
        ),
      };
    }),
);

export const getStatusesByScheduleLineGroupGuid = createSelector(
  (state: RootState) => state.scheduleLineGroups,
  getBundlesByScheduleLineGroupGuid,
  getStatusesByBundleGuid,

  (scheduleLineGroups, bundlesByScheduleLineGroupGuid, statusesByBundleGuid) =>
    mapValues(scheduleLineGroups, (scheduleLineGroup) =>
      uniq(
        bundlesByScheduleLineGroupGuid[scheduleLineGroup.guid]?.flatMap(
          (bundle) => statusesByBundleGuid[bundle.guid],
        ) ?? [],
      ),
    ),
);
